import z from "zod";
import { StockPolicyIdScheme } from "./ids";
import { StockPolicyMode } from "./enums/StockPolicyMode";

const StockLevelScheme = z.number().min(0).nullable();

type StockLevels = {
  min?: number | null;
  max?: number | null;
};

const areStockLevelsInOrder = (levels: StockLevels) =>
  levels.min === null ||
  levels.min === undefined ||
  levels.max === null ||
  levels.max === undefined ||
  levels.min <= levels.max;

export const areStockPolicyLevelsInOrder = (
  policy: StockLevels & { periods?: StockLevels[] },
) =>
  areStockLevelsInOrder(policy) &&
  (policy.periods ?? []).every((period) =>
    areStockLevelsInOrder({
      min: period.min ?? policy.min,
      max: period.max ?? policy.max,
    }),
  );

const LEVELS_ORDER_ERROR = {
  message: "Minimum must not be above fill-up level",
  path: ["max"],
};

export const StockPolicyPeriodScheme = z
  .object({
    start: z.iso.date(),
    end: z.iso.date(),
    repeatsYearly: z.boolean(),
    min: StockLevelScheme.optional(),
    max: StockLevelScheme.optional(),
  })
  .refine((period) => period.repeatsYearly || period.start <= period.end, {
    message: "Period start must not be after its end",
    path: ["end"],
  })
  .refine(areStockLevelsInOrder, LEVELS_ORDER_ERROR);

export const StockPolicyDBScheme = z.object({
  id: StockPolicyIdScheme,
  name: z.string().trim().min(1),
  mode: z.enum(StockPolicyMode),
  min: StockLevelScheme,
  max: StockLevelScheme,
  warnAboveMax: z.boolean(),
  periods: z.array(StockPolicyPeriodScheme),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const StockPolicyAssignmentScheme = z.object({
  policyId: StockPolicyIdScheme.nullable(),
  min: StockLevelScheme.optional(),
  max: StockLevelScheme.optional(),
});

export const RestGetStockPolicy = StockPolicyDBScheme;

const StockPolicyFieldsScheme = StockPolicyDBScheme.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const RestCreateStockPolicy = StockPolicyFieldsScheme.refine(
  areStockPolicyLevelsInOrder,
  LEVELS_ORDER_ERROR,
);

export const RestUpdateStockPolicy = StockPolicyFieldsScheme.partial();

export type DBStockPolicy = z.infer<typeof StockPolicyDBScheme>;
export type StockPolicyPeriod = z.infer<typeof StockPolicyPeriodScheme>;
export type StockPolicyAssignment = z.infer<typeof StockPolicyAssignmentScheme>;
export type RestStockPolicy = z.infer<typeof RestGetStockPolicy>;
export type RestCreateStockPolicy = z.infer<typeof RestCreateStockPolicy>;
export type RestUpdateStockPolicy = z.infer<typeof RestUpdateStockPolicy>;
